/**
 * Auth Service endpoints (spec §10.2, §10.3, §10.7–§10.12). Thin wrappers around
 * `httpClient`; no business logic. OAuth `authorize`/`callback` are NOT called
 * via fetch — they require top-level browser navigation (spec §10.8).
 */
import { env } from '../lib/env';
import type { ProviderId } from './apiSchemas';
import {
  identitiesResponseSchema,
  linkStartResponseSchema,
  logoutAllResponseSchema,
  providersResponseSchema,
  sessionResponseSchema,
  type IdentitiesResponse,
  type LinkStartRequest,
  type LinkStartResponse,
  type LogoutAllResponse,
  type ProvidersResponse,
  type SessionResponse,
} from './apiSchemas';
import { httpClient } from './httpClient';

export function getSession(signal?: AbortSignal): Promise<SessionResponse> {
  return httpClient.get('/auth/session', sessionResponseSchema, signal);
}

export function getProviders(
  client: string | null,
  signal?: AbortSignal,
): Promise<ProvidersResponse> {
  const qs = client ? `?client=${encodeURIComponent(client)}` : '';
  return httpClient.get(`/auth/providers${qs}`, providersResponseSchema, signal);
}

export function getIdentities(signal?: AbortSignal): Promise<IdentitiesResponse> {
  return httpClient.get('/auth/identities', identitiesResponseSchema, signal);
}

/**
 * Builds the absolute social-authorize URL for a top-level browser navigation
 * (spec §10.8). `client`/`return_route` are passed verbatim for the backend to
 * validate; the frontend does not redirect using `return_route` itself.
 */
export function buildAuthorizeUrl(
  provider: ProviderId,
  params: { client: string | null; returnRoute: string | null },
): string {
  // Resolve against the current origin so a relative apiBaseUrl ("/api") also works.
  const url = new URL(
    `${env.apiBaseUrl}/auth/social/${provider}/authorize`,
    window.location.origin,
  );
  if (params.client) url.searchParams.set('client', params.client);
  if (params.returnRoute) url.searchParams.set('return_route', params.returnRoute);
  return url.toString();
}

export function linkStart(
  provider: ProviderId,
  body: LinkStartRequest,
): Promise<LinkStartResponse> {
  return httpClient.post(`/auth/identities/${provider}/link-start`, {
    schema: linkStartResponseSchema,
    body,
  });
}

export function unlinkIdentity(provider: ProviderId): Promise<void> {
  return httpClient.del(`/auth/identities/${provider}`);
}

export function logout(): Promise<void> {
  return httpClient.post('/auth/logout');
}

export function logoutAll(): Promise<LogoutAllResponse> {
  return httpClient.post('/auth/logout-all', { schema: logoutAllResponseSchema });
}
